/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import _ from 'underscore';

import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { viewerZIndices } from '../../../../viewer/viewer-z-indices';
import { useDialog } from '../../../hooks/use-dialog';
import { useSharedView } from '../../../hooks/use-shared-view';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { getSelectedProjectId } from '../../../routing/utils';
import { validSkyViewFileExtensions } from '../../../utils/file';
import { reset } from '../../../utils/styled-reset';
import { Button } from '../../button/button';
import { FileManagerDialog } from '../../file-manager-dialog/file-manager-dialog';
import { Icon } from '../../icon/icon';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { CategoryState } from '../state/category-state';
import { useAssetFilterStore } from '../state/asset-filter-store';
import { AssetMenuList } from './asset-menu-list';
import { FilterDialog } from './components/filter-dialog';

const AssetMenu = () => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = React.useState('');
  const [showAssetMenuOverlay, setShowAssetMenuOverlay] = React.useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = React.useState(false);

  const fileManagerDialog = useDialog();
  const { isFiltering, filtersEnabled } = useAssetFilterStore();
  const isFilterActive = isFiltering && filtersEnabled;
  const { isSharedView } = useSharedView();

  const debounceSearchTextInput = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setSearchText(value);
      }, 200),
    [],
  );

  useSubscribe(SubscriptionTopic.ShowAssetMenuOverlay, () => {
    setShowAssetMenuOverlay(true);
  });

  useSubscribe(SubscriptionTopic.HideAssetMenuOverlay, () => {
    setShowAssetMenuOverlay(false);
  });

  return (
    <>
      <CategoryState>
        <OverlayLoader backgroundColor="#FFFA" height="100%" visible={showAssetMenuOverlay}>
          <Component>
            <SearchContainer direction="row">
              <TextBox
                placeholder={t('topArea.searchFieldWatermark', { ns: 'skyviewAssetMenu' })}
                onChange={(e) => debounceSearchTextInput(e.target.value)}
              />
              <FilterIconButton
                $active={isFilterActive}
                $open={isFilterDialogOpen}
                aria-label="Filter"
                data-active={isFilterActive}
                data-open={isFilterDialogOpen}
                title={t('topArea.filter.dialogTitle', { ns: 'skyviewAssetMenu' })}
                onClick={() => setIsFilterDialogOpen((v) => !v)}
              >
                <Icon icon={['fal', 'filter-list']} />
              </FilterIconButton>
            </SearchContainer>
            <AssetMenuList assetNameFilter={searchText} />
            {!isSharedView && (
              <Footer>
                <Button
                  color="primary"
                  variant="contained"
                  width="100%"
                  onClick={() => fileManagerDialog.show()}
                >
                  {t('bottomArea.openFileManagerButton', { ns: 'skyviewAssetMenu' })}
                </Button>
              </Footer>
            )}
          </Component>
        </OverlayLoader>
      </CategoryState>

      {fileManagerDialog.render(
        <FileManagerDialog
          defaultFileUploadExtensions={validSkyViewFileExtensions}
          onClose={() => {
            fileManagerDialog.hide();

            const projectId = getSelectedProjectId();
            publish(SubscriptionTopic.RefreshAssetList, {
              projectId: projectId!,
              assetTypes: ['Dxf', 'Glb'],
            });
          }}
        />,
      )}

      {/* Persistent filter dialog */}
      <FilterDialog open={isFilterDialogOpen} />
    </>
  );
};

const Footer = styled.div`
  padding: 0 var(--padding) var(--padding) var(--padding);
`;

const Component = styled.div`
  ${reset}

  --padding: 0.8em;

  display: grid;
  grid-template-rows: auto 1fr auto;
  row-gap: var(--padding);

  height: 100%;

  z-index: ${viewerZIndices.menu};
  background-color: #fff;
  font-size: 14px;
  padding: var(--padding) 0 0 0;
  ${AssetMenuList.styled} {
    padding: 0 var(--padding);
  }

  // Add bottom space to asset menu list if no footer exists.
  &:not(&:has(${Footer})) {
    ${AssetMenuList.styled} {
      padding-bottom: var(--padding);
    }
  }
`;

const SearchContainer = styled(Stack)`
  margin: 0 var(--padding);
`;

export { AssetMenu };

const FilterIconButton = styled.button<{ $active: boolean; $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${(p) => p.theme.color.gray.darkest};
  border-left: none;
  cursor: pointer;
  position: relative;

  color: ${(p) => p.theme.color.gray.darkest};

  /* Invert colors when dialog is open */
  ${(p) =>
    p.$open &&
    `
      background-color: ${p.theme.color.gray.darkest};
      color: white;
      border-color: ${p.theme.color.gray.darkest};
    `}

  /* Active badge dot */
  &::after {
    content: '';
    display: ${(p) => (p.$active ? 'block' : 'none')};
    position: absolute;
    top: -0.3em;
    right: -0.3em;
    width: 0.6em;
    height: 0.6em;
    border-radius: 50%;
    background-color: ${(p) => p.theme.color.brand.dark};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    ${(p) => (p.$open ? '' : `background-color: ${p.theme.color.gray.light};`)}
  }
`;
